import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { StaffPrincipal } from '@/types/staff'

/**
 * Staff identity/session state — populated by the real staffauth OIDC +
 * passkey + MFA login flow (`views/auth/MfaStepView.vue`, the ceremony's
 * final step, calls `setSession()` with `POST /auth/mfa/verify`'s response
 * plus a follow-up `GET /auth/me`).
 *
 * `token` is held IN MEMORY ONLY — never `localStorage`/`sessionStorage` —
 * a deliberate choice for this internal ops console:
 *   - the bearer token is a genuine high-privilege credential (unlike the
 *     login ceremony's short-lived, single-use, non-secret correlation id
 *     in `stores/login-ceremony.ts`, which IS sessionStorage-backed);
 *   - `StaffSessionTTL` defaults to 30 minutes (`config.go`), short enough
 *     that losing the session on a page reload/tab close is an acceptable
 *     trade for never having a real credential sit in Web Storage, where an
 *     XSS bug would make it trivially exfiltratable and durable across
 *     reloads/tabs;
 *   - this mirrors the OWASP SPA guidance of preferring in-memory token
 *     storage over `localStorage`/`sessionStorage` for exactly this reason,
 *     and fits an internal, VPN/IP-allowlisted, short-session tool better
 *     than it would a consumer app where "stay signed in" UX matters more.
 * A page reload therefore always requires signing in again — by design.
 */
export const useSessionStore = defineStore('session', () => {
  const principal = ref<StaffPrincipal | null>(null)
  const token = ref<string | null>(null)
  const tokenExpiresAt = ref<string | null>(null)

  const isAuthenticated = computed(() => principal.value !== null && token.value !== null)

  function setSession(nextPrincipal: StaffPrincipal, nextToken: string, expiresAt: string) {
    principal.value = nextPrincipal
    token.value = nextToken
    tokenExpiresAt.value = expiresAt
  }

  function clear() {
    principal.value = null
    token.value = null
    tokenExpiresAt.value = null
  }

  // Setup-store convention: return every piece of state (not just actions/
  // getters) so DevTools, SSR and Pinia plugins can see it all.
  return { principal, token, tokenExpiresAt, isAuthenticated, setSession, clear }
})
